import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useIsWorkerPaused } from "@/components/admin/WorkerControls";
import { cancelInnovaAlgoTradesByPtuIntradayAlgoInnovaAlgoTradesByPtuDelete as cancelPtuOrders } from "@/client-admin/services.gen";

interface CancelPtuOrdersDialogProps {
  deliveryDate: string;
  start: string;
  end: string;
}

export function CancelPtuOrdersDialog({ deliveryDate, start, end }: CancelPtuOrdersDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const canCancel = useIsWorkerPaused();

  const { mutateAsync: cancelPtuMutation, isPending } = useMutation({
    mutationFn: () =>
      cancelPtuOrders({
        requestBody: {
          delivery_date: deliveryDate,
          start,
          end,
        },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("intraday-algo") });
      toast({
        title: "PTU orders cancelled",
        description:
          `Cancelled ${data.cancelled} innova algo trade(s), ` + `reset ${data.signals_reset} signal(s) to Algo=0.`,
        duration: Infinity,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.body?.detail ?? "Failed to cancel PTU orders. Please try again later.",
        variant: "destructive",
      });
    },
  });

  const timeRange = `${start.slice(0, 5)}–${end.slice(0, 5)}`;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="destructive"
          disabled={!canCancel}
          title={canCancel ? undefined : "Pause the worker before cancelling PTU orders"}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Cancel PTU Orders
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel PTU Orders — {timeRange}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>This will:</p>
              <ol className="ml-4 list-decimal space-y-1">
                <li>
                  Cancel ETPA orders for active innova algo trades on this PTU and mark all pending/active trades as{" "}
                  <strong>CANCELLED</strong>.
                </li>
                <li>Reset signals with Algo=2 for this PTU back to Algo=0 so you can re-flow via Algo=1 + rebuild.</li>
              </ol>
              <p>Are you sure?</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={async () => {
              await cancelPtuMutation();
              setOpen(false);
            }}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Confirm Cancel"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
